import { useState } from "react";
import {
    Popover,
    PopoverContent,
    PopoverTriggerButton,
} from "@design-system/Popover/Popover";
import {
    BooleanControl,
    NumberControl,
    PlaygroundSelect,
} from "./playgroundControls";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";
import styles from "./OverlayPlayground.module.scss";

const popoverSides = ["top", "right", "bottom", "left"] as const;
type PopoverSide = (typeof popoverSides)[number];

const popoverAligns = ["start", "center", "end"] as const;
type PopoverAlign = (typeof popoverAligns)[number];

const clampSideOffset = (value: number) =>
    Number.isFinite(value) ? Math.min(Math.max(value, 0), 32) : undefined;

export const PopoverPlayground = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [side, setSide] = useState<PopoverSide>("bottom");
    const [align, setAlign] = useState<PopoverAlign>("center");
    const [sideOffset, setSideOffset] = useState(8);
    const [hasArrow, setHasArrow] = useState(true);
    const [isModal, setIsModal] = useState(false);

    return (
        <SandboxLayout
            controls={
                <>
                    <section>
                        <h2>Position</h2>
                        <PlaygroundSelect<PopoverSide>
                            options={popoverSides}
                            value={side}
                            onChange={setSide}
                        />
                        <h2 className={styles.subheading}>Align</h2>
                        <PlaygroundSelect<PopoverAlign>
                            options={popoverAligns}
                            value={align}
                            onChange={setAlign}
                        />
                        <h2 className={styles.subheading}>Offset</h2>
                        <NumberControl
                            label="Pixels"
                            value={sideOffset}
                            min={0}
                            max={32}
                            onChange={(value) => {
                                const nextSideOffset = clampSideOffset(value);
                                if (nextSideOffset !== undefined) {
                                    setSideOffset(nextSideOffset);
                                }
                            }}
                        />
                    </section>
                    <section>
                        <h2>Features</h2>
                        <div className={styles.controls}>
                            <BooleanControl
                                label="Trigger arrow"
                                checked={hasArrow}
                                onChange={setHasArrow}
                            />
                            <BooleanControl
                                label="Modal interaction"
                                checked={isModal}
                                onChange={setIsModal}
                            />
                        </div>
                    </section>
                </>
            }
        >
            <ShowcasePreview
                summary={`${side} · ${align}`}
                output={JSON.stringify(
                    {
                        side,
                        align,
                        sideOffset,
                        modal: isModal,
                        trigger: { arrow: hasArrow },
                        showcase: { isOpen },
                    },
                    null,
                    2,
                )}
            >
                <Popover open={isOpen} onOpenChange={setIsOpen} modal={isModal}>
                    <PopoverTriggerButton arrow={hasArrow}>
                        Open popover
                    </PopoverTriggerButton>
                    <PopoverContent
                        side={side}
                        align={align}
                        sideOffset={sideOffset}
                        className={styles.popoverContent}
                    >
                        <strong>Transaction options</strong>
                        <p>
                            Position this panel around its trigger with the
                            controls.
                        </p>
                    </PopoverContent>
                </Popover>
            </ShowcasePreview>
        </SandboxLayout>
    );
};
